/**
 * 基于 navigator.userAgent 的运行环境解析（共用）。
 * UA 可被篡改或演进，结果仅供参考；服务端渲染时请传入 ua 或做空字符串兜底。
 */

/**
 * @returns {string}
 */
export function getUserAgentString () {
  if (typeof navigator === 'undefined' || navigator.userAgent == null) return ''
  return String(navigator.userAgent)
}

/**
 * @param {string} [ua] 不传则用当前页面 UA（SSR 时请传入请求头 UA）
 * @returns {{
 *   raw: string,
 *   os: 'windows'|'macos'|'linux'|'android'|'ios'|'unknown',
 *   osVersion: string,
 *   browser: 'chrome'|'safari'|'firefox'|'edge'|'wechat'|'micromessenger'|'unknown',
 *   browserVersion: string,
 *   isMobileDevice: boolean,
 *   isWeChat: boolean,
 * }}
 */
const SAFE_FALLBACK = () => ({
  raw: '',
  os: 'unknown',
  osVersion: '',
  browser: 'unknown',
  browserVersion: '',
  isMobileDevice: true,
  isWeChat: false
})

export function parseUserAgent (ua) {
  try {
    return parseUserAgentUnsafe(ua != null ? String(ua) : getUserAgentString())
  } catch {
    const fb = SAFE_FALLBACK()
    fb.raw = ua != null ? String(ua) : getUserAgentString()
    return fb
  }
}

function parseUserAgentUnsafe (raw) {
  /** @type {'windows'|'macos'|'linux'|'android'|'ios'|'unknown'} */
  let os = 'unknown'
  let osVersion = ''

  if (/windows nt/i.test(raw)) {
    os = 'windows'
    const m = raw.match(/windows nt ([\d._]+)/i)
    osVersion = m ? m[1].replace(/_/g, '.') : ''
  } else if (/iphone|ipad|ipod/i.test(raw)) {
    os = 'ios'
    const m = raw.match(/os (\d+[._]\d+(?:[._]\d+)?)/i)
    osVersion = m ? m[1].replace(/_/g, '.') : ''
  } else if (/macintosh|mac os x/i.test(raw)) {
    os = 'macos'
    const m = raw.match(/mac os x (\d+[._]\d+(?:[._]\d+)?)/i)
    osVersion = m ? m[1].replace(/_/g, '.') : ''
  } else if (/android/i.test(raw)) {
    os = 'android'
    const m = raw.match(/android (\d+(?:\.\d+)*)/i)
    osVersion = m ? m[1] : ''
  } else if (/linux/i.test(raw)) {
    os = 'linux'
  }

  let browser = 'unknown'
  let browserVersion = ''

  if (/micromessenger/i.test(raw)) {
    browser = 'wechat'
    const m = raw.match(/micromessenger\/([\d.]+)/i)
    browserVersion = m ? m[1] : ''
  } else if (/edg\//i.test(raw)) {
    browser = 'edge'
    const m = raw.match(/edg\/([\d.]+)/i)
    browserVersion = m ? m[1] : ''
  } else if (/chrome|crios/i.test(raw) && !/edg/i.test(raw)) {
    browser = 'chrome'
    const m = raw.match(/(?:chrome|crios)\/([\d.]+)/i)
    browserVersion = m ? m[1] : ''
  } else if (/safari/i.test(raw) && !/chrome|crios/i.test(raw)) {
    browser = 'safari'
    const m = raw.match(/version\/([\d.]+)/i)
    browserVersion = m ? m[1] : ''
  } else if (/firefox|fxios/i.test(raw)) {
    browser = 'firefox'
    const m = raw.match(/(?:firefox|fxios)\/([\d.]+)/i)
    browserVersion = m ? m[1] : ''
  }

  /** iPadOS 13+ 桌面模式 UA 常为 Macintosh，用触控点数辅助识别 */
  const touchMacintoshTablet =
    typeof navigator !== 'undefined' &&
    navigator.maxTouchPoints > 1 &&
    /macintosh/i.test(raw)

  const isMobileDevice =
    touchMacintoshTablet ||
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(raw) ||
    (os === 'android' || os === 'ios')

  const isWeChat = /micromessenger/i.test(raw)

  return {
    raw,
    os,
    osVersion,
    browser,
    browserVersion,
    isMobileDevice,
    isWeChat
  }
}

/** 当前页面 UA 解析结果（模块级缓存，避免重复拆分） */
let _parsedCache = null

export function getParsedUserAgent () {
  if (_parsedCache == null) {
    try {
      _parsedCache = parseUserAgent()
    } catch {
      _parsedCache = SAFE_FALLBACK()
      _parsedCache.raw = getUserAgentString()
    }
  }
  return _parsedCache
}

/** 清空缓存（单测或切换 UA mock 时可调用） */
export function clearParsedUserAgentCache () {
  _parsedCache = null
}
